import { SearchOutlined } from '@ant-design/icons'
import { Input, Tree } from 'antd'
// @ts-ignore
import * as d3hierarchy from 'd3-hierarchy'
import { useAtom } from 'jotai'
import { debounce, flatMapDeep } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Department, departmentsTreeAtom } from '@/modules/department'

export const useDepartmentTree = ({ currentDepartmentId }: { currentDepartmentId: number }) => {
  const { i18n } = useTranslation()
  const [departments] = useAtom(departmentsTreeAtom)

  const department = departments.find((d) => d.id === currentDepartmentId)

  const treeData = React.useMemo(() => {
    const treeData = d3hierarchy
      .stratify()
      .id((d: Department) => d.id)
      .parentId((d: Department) => (d.parentId === 0 ? null : d.parentId))(departments)
    treeData.each((d: any) => {
      d.key = d.data.id
      d.title = i18n.resolvedLanguage === 'zh' ? d.data.name : d.data.ename
    })
    return treeData
  }, [departments])

  const parentIds = department
    ? treeData
        .descendants()
        .find((d: any) => d.data.id === department.id)
        .ancestors()!
        .map((d: any) => d.data.id)
    : []

  const departmentId = department?.id

  return {
    department,
    departmentId,
    treeData,
    parentIds,
  }
}

const flatten = (item: any): any[] => [item, flatMapDeep(item.children, flatten)]

interface DepartmentTreeProps {
  treeData: any
  selectedKeys: number[]
  onChangeKey: (key: number) => void
  showSearch?: boolean
}

export const DepartmentTree: React.FC<DepartmentTreeProps> = ({
  treeData,
  selectedKeys,
  onChangeKey,
  showSearch = false,
}) => {
  const { t } = useTranslation()
  const [expandedKeys, setExpandedKeys] = React.useState(selectedKeys)
  const [searchKey, setSearchKey] = React.useState('')

  const onSearchChange = debounce((value: string) => {
    setSearchKey(value)
    const data = flatMapDeep(treeData.children, flatten)
    if (value) {
      value = value.toLowerCase()
      const expandedKeys = data
        .filter(
          (d) => d.data.name.toLowerCase().indexOf(value) !== -1 || d.data.ename.toLowerCase().indexOf(value) !== -1
        )
        .map((d) => d.data.parentId)

      setExpandedKeys(expandedKeys)
    } else {
      setExpandedKeys(selectedKeys)
    }
  }, 200)

  const onExpand = (expandedKeys: any[]) => {
    setExpandedKeys(expandedKeys)
  }

  const loopDataTitle = (data: any[]): any[] =>
    data.map((item) => {
      const index = item.title.toLowerCase().indexOf(searchKey.toLowerCase())
      const beforeStr = item.title.substring(0, index)
      const value = item.title.substring(index, index + searchKey.length)
      const afterStr = item.title.slice(index + searchKey.length)
      const title =
        index > -1 ? (
          <span>
            {beforeStr}
            <span className='text-red-500'>{value}</span>
            {afterStr}
          </span>
        ) : (
          <span>{item.title}</span>
        )
      if (item.children) {
        return { title, key: item.key, children: loopDataTitle(item.children) }
      }

      return {
        title,
        key: item.key,
      }
    })

  return (
    <div className='pr-20px relative overflow-hidden'>
      {showSearch && (
        <Input
          className='w-240px z-3 fixed'
          placeholder={t('搜索部门')}
          prefix={<SearchOutlined className='text-gray-500' />}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      )}
      <Tree
        className='flex-shrink-0 mt-40px w-240px overflow-y-auto'
        autoExpandParent
        multiple={false}
        treeDataSimpleMode
        defaultExpandedKeys={selectedKeys}
        expandedKeys={expandedKeys}
        selectedKeys={selectedKeys}
        onSelect={(keys) => {
          if (keys.length > 0) {
            onChangeKey(keys[0] as number)
          }
        }}
        onExpand={onExpand}
        treeData={loopDataTitle(treeData.children)}
      />
    </div>
  )
}
